import React, { useEffect, useState } from 'react';
import ApiService from '../../../services/api-service';
import { __ } from '../../../services/translation';
import { formatDateTime } from '../../../utils/format-date';
import Spinner from '../Spinner';
import './PriceHistory.scss';

interface PriceHistoryEntry {
    id: string;
    changedAt: string;
    pricePerKg: number | null;
    price: number;
    weight: number;
}

interface Props {
    productId: string;
    showPricePerKg?: boolean;
}

const PriceHistory = ({ productId, showPricePerKg = false }: Props) => {
    const [history, setHistory] = useState<PriceHistoryEntry[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        ApiService.callFetch('GET', `product/price-history/${productId}`, (data: PriceHistoryEntry[]) => {
            if (!cancelled) {
                setHistory(data);
                setLoading(false);
            }
        }, () => {
            if (!cancelled) {
                setLoading(false);
            }
        });

        return () => { cancelled = true; };
    }, [productId]);

    const formatNumber = (n: number) => {
        return n.toFixed(4).replace(/\.?0+$/, '');
    };

    if (loading) {
        return (
            <div className="price-history">
                <h3 className="price-history__title">{__('price_history.title')}</h3>
                <Spinner small />
            </div>
        );
    }

    return (
        <div className="price-history">
            <h3 className="price-history__title">{__('price_history.title')}</h3>
            {history.length === 0 ? (
                <p className="price-history__empty">{__('price_history.empty')}</p>
            ) : (
                <div className="price-history__table-wrapper">
                    <table className="price-history__table">
                        <thead>
                            <tr>
                                <th>{__('price_history.date')}</th>
                                {showPricePerKg && <th>{__('price_history.price_per_kg')}</th>}
                                <th>{__('price_history.price')}</th>
                                <th>{__('price_history.weight')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((entry, index) => (
                                <tr key={entry.id} className={index === 0 ? 'price-history__current' : ''}>
                                    <td>{formatDateTime(entry.changedAt)}</td>
                                    {showPricePerKg && (
                                        <td>
                                            {entry.pricePerKg !== null
                                                ? `${formatNumber(entry.pricePerKg)} zł/kg`
                                                : '—'}
                                        </td>
                                    )}
                                    <td>{formatNumber(entry.price)} zł/m</td>
                                    <td>{formatNumber(entry.weight)} kg/m</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PriceHistory;
